import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import {
  TIMEZONES,
  TimezoneOption,
  getTimezoneLabel,
  getUserTimezone,
} from '../../utils/timezones';
import useTranslation from '../../hooks/useTranslation';

interface TimezoneSelectorProps {
  selectedTimezone: string;
  onTimezoneChange: (timezone: string) => void;
}

const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  selectedTimezone,
  onTimezoneChange,
}) => {
  const { t } = useTranslation();
  const [userTimezone, setUserTimezone] = useState<string>('');

  useEffect(() => {
    const detectedTimezone = getUserTimezone();
    setUserTimezone(detectedTimezone);

    if (!selectedTimezone) {
      onTimezoneChange(detectedTimezone);
    }
  }, [selectedTimezone, onTimezoneChange]);

  const selectedTimezoneOption =
    TIMEZONES.find(tz => tz.value === selectedTimezone) || null;

  // Sort timezones alphabetically within each group
  const sortedTimezones = [...TIMEZONES].sort((a, b) => {
    // First sort by group
    if (a.group !== b.group) {
      return a.group.localeCompare(b.group);
    }
    // Then sort by label within the same group
    return a.label.localeCompare(b.label);
  });

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {t('contact.booking.selectTimezone')}
      </Typography>
      <Autocomplete
        options={sortedTimezones}
        value={selectedTimezoneOption}
        onChange={(_, newValue: TimezoneOption | null) => {
          if (newValue) {
            onTimezoneChange(newValue.value);
          } else {
            onTimezoneChange(userTimezone);
          }
        }}
        getOptionLabel={(option: TimezoneOption) => option.label}
        groupBy={(option: TimezoneOption) => option.group}
        renderInput={params => (
          <TextField
            {...params}
            label={t('contact.booking.selectTimezone')}
            fullWidth
          />
        )}
        isOptionEqualToValue={(option: TimezoneOption, value: TimezoneOption) =>
          option.value === value.value
        }
      />
      {userTimezone && userTimezone !== selectedTimezone && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Your detected timezone: {getTimezoneLabel(userTimezone)}
        </Typography>
      )}
    </Box>
  );
};

export default TimezoneSelector;
